import { useEffect, useRef, useState, useCallback } from "react";

const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];

/**
 * Tracks user inactivity and fires onTimeout after timeoutMinutes of no activity.
 * Shows a warning countdown during the last warnBeforeSeconds seconds.
 *
 * @param {number} timeoutMinutes  - 0 or null disables the timer
 * @param {function} onTimeout     - called when inactivity period expires
 * @returns {{ warningVisible, secondsLeft, resetTimer }}
 */
export function useInactivityTimer(timeoutMinutes, onTimeout) {
  const [secondsLeft, setSecondsLeft] = useState(null);
  const lastActivityRef = useRef(Date.now());
  const onTimeoutRef    = useRef(onTimeout);

  // Keep callback ref current without re-running the effect
  useEffect(() => { onTimeoutRef.current = onTimeout; }, [onTimeout]);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    setSecondsLeft(null);
  }, []);

  useEffect(() => {
    if (!timeoutMinutes || timeoutMinutes <= 0) {
      setSecondsLeft(null);
      return;
    }

    const timeoutMs    = timeoutMinutes * 60 * 1000;
    // Warn at 60 s before logout, but never more than half the total timeout
    const warnBeforeMs = Math.min(60 * 1000, timeoutMs / 2);

    const handleActivity = () => { lastActivityRef.current = Date.now(); };
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));

    const interval = setInterval(() => {
      const elapsed   = Date.now() - lastActivityRef.current;
      const remaining = timeoutMs - elapsed;

      if (remaining <= 0) {
        onTimeoutRef.current();
      } else if (remaining <= warnBeforeMs) {
        setSecondsLeft(Math.ceil(remaining / 1000));
      } else {
        setSecondsLeft(null);
      }
    }, 1000);

    return () => {
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, handleActivity));
      clearInterval(interval);
    };
  }, [timeoutMinutes]);

  return { warningVisible: secondsLeft !== null, secondsLeft, resetTimer };
}
